import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSliceBalance } from '../hooks/useSliceBalance';
import { useSlicePot } from '../hooks/useSlicePot';
import { useCountdown } from '../components/pot/useCountdown';
import PotVoteModal from '../components/pot/PotVoteModal';
import PastFundWinners from '../components/pot/PastFundWinners';
import RailHeader from '../components/home/RailHeader';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, Users, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Countdown segment ──────────────────────────────────────── */
const CountSegment = ({ value, label }) => (
  <div className="flex items-baseline gap-0.5">
    <span className="text-2xl md:text-3xl font-semibold tabular-nums text-foreground">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-0.5">
      {label}
    </span>
  </div>
);

/* ─── Loading skeleton ────────────────────────────────────────── */
const LeaderboardSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="rounded-xl border border-border/40 h-40 bg-card/20" />
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-lg border border-border/40 overflow-hidden">
          <div className="aspect-[2/3] bg-muted/40" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-3 bg-muted rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Empty state ────────────────────────────────────────────── */
const EmptyNominees = () => (
  <div className="rounded-lg border border-dashed border-border/60 py-16 px-6 text-center space-y-4">
    <p className="font-semibold text-foreground">No nominees yet this month</p>
    <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
      Be the first to nominate a work you believe in — at month end the whole
      pot goes to the work the community backs most.
    </p>
    <Button asChild className="rounded-full h-11 px-6">
      <Link to="/browse">
        <Sparkles className="mr-2 h-4 w-4" />
        Browse works to nominate
      </Link>
    </Button>
  </div>
);

/* ─── Rank badge ─────────────────────────────────────────────── */
const RankBadge = ({ rank, highlight }) => (
  <span
    className={cn(
      'inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums shrink-0',
      highlight
        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
        : 'bg-secondary text-muted-foreground border border-border/60'
    )}
  >
    {rank}
  </span>
);

/* ─── Vote stats line ────────────────────────────────────────── */
const VoteStats = ({ entry }) => (
  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground tabular-nums">
    <span className="inline-flex items-center gap-1">
      <TrendingUp className="h-3 w-3 text-amber-400" />
      <span className="text-amber-400 font-semibold">
        {(entry.votes ?? 0).toLocaleString()}
      </span>
      votes
    </span>
    <span className="inline-flex items-center gap-1">
      <Users className="h-3 w-3" />
      {(entry.voters ?? 0).toLocaleString()} voters
    </span>
    <span>{(entry.nominations ?? 0).toLocaleString()} nominations</span>
  </div>
);

/* ─── #1 featured card ───────────────────────────────────────── */
const LeadCard = ({ entry, onVote }) => (
  <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-card/20">
    {entry.poster_url && (
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <img
          src={entry.poster_url}
          alt=""
          className="w-full h-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/50" />
      </div>
    )}

    <div className="relative p-5 sm:p-6 flex items-center gap-5 sm:gap-8">
      {entry.poster_url && (
        <img
          src={entry.poster_url}
          alt={entry.title}
          className="hidden sm:block w-24 md:w-28 aspect-[2/3] object-cover rounded-lg border border-border/40 shrink-0"
        />
      )}

      <div className="flex-1 min-w-0 space-y-3">
        <div className="flex items-center gap-2.5">
          <RankBadge rank={1} highlight />
          <span className="text-[10px] uppercase tracking-widest text-amber-500 font-medium inline-flex items-center gap-1.5">
            <Trophy className="h-3 w-3" />
            Leading this month
          </span>
        </div>

        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-foreground leading-snug">
            {entry.title}
          </h3>
          {entry.publisher?.company_name && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {entry.publisher.company_name}
            </p>
          )}
        </div>

        <VoteStats entry={entry} />

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button
            className="h-10 px-5 rounded-full text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white border-0 shadow-lg shadow-amber-500/20"
            onClick={() => onVote(entry)}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Vote with slices
          </Button>
          {entry.slug && (
            <Link
              to={`/watch/${entry.slug}`}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              View work →
            </Link>
          )}
        </div>
      </div>
    </div>
  </div>
);

/* ─── Ranked card (2–5) ──────────────────────────────────────── */
const RankedCard = ({ entry, rank, onVote }) => (
  <div className="rounded-lg border border-border/40 overflow-hidden bg-card/20 flex flex-col hover:border-amber-500/20 transition-colors">
    {entry.poster_url ? (
      <div className="relative aspect-[2/3] bg-muted overflow-hidden">
        <img
          src={entry.poster_url}
          alt={entry.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-2 left-2">
          <RankBadge rank={rank} />
        </div>
      </div>
    ) : (
      <div className="relative aspect-[2/3] bg-muted/40 flex items-center justify-center">
        <div className="absolute top-2 left-2">
          <RankBadge rank={rank} />
        </div>
      </div>
    )}

    <div className="p-3.5 flex flex-col gap-2.5 flex-1">
      <div className="min-w-0">
        <h3 className="font-semibold text-sm leading-snug text-foreground line-clamp-2">
          {entry.title}
        </h3>
        {entry.publisher?.company_name && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {entry.publisher.company_name}
          </p>
        )}
      </div>

      <VoteStats entry={entry} />

      <Button
        size="sm"
        className="mt-auto h-9 rounded-full text-xs font-medium bg-amber-500/15 text-amber-500 border border-amber-500/30 hover:bg-amber-500 hover:text-white"
        onClick={() => onVote(entry)}
      >
        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
        Vote
      </Button>
    </div>
  </div>
);

/* ─── Compact nominee row (6+) ───────────────────────────────── */
const NomineeRow = ({ entry, rank, onVote }) => (
  <li className="flex items-center gap-4 py-3 px-4">
    <RankBadge rank={rank} />

    {entry.poster_url ? (
      <img
        src={entry.poster_url}
        alt={entry.title}
        className="h-14 w-10 object-cover rounded shrink-0"
      />
    ) : (
      <div className="h-14 w-10 rounded bg-muted/40 shrink-0" />
    )}

    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm text-foreground truncate">{entry.title}</p>
      <div className="mt-1">
        <VoteStats entry={entry} />
      </div>
    </div>

    <Button
      size="sm"
      variant="outline"
      className="h-8 px-4 rounded-full text-xs shrink-0"
      onClick={() => onVote(entry)}
    >
      Vote
    </Button>
  </li>
);

/* ─── Page ───────────────────────────────────────────────────── */
const CommunityFund = () => {
  const { isAuthenticated } = useAuth();
  const { balance, isActiveSubscriber } = useSliceBalance();
  const { worksSummary, pastWorkWinners, isLoading, refetch, voteWork } = useSlicePot();

  const [voteTarget, setVoteTarget] = useState(null);

  const leaderboard = worksSummary?.leaderboard ?? [];
  const potBalance  = worksSummary?.pot_balance ?? 0;
  const period      = worksSummary?.period ?? null;
  const countdown   = useCountdown(period?.ends_at ?? null);

  const monthLabel = period?.starts_at
    ? new Date(period.starts_at).toLocaleDateString(undefined, {
        month: 'long',
        year:  'numeric',
      })
    : null;

  const top5      = leaderboard.slice(0, 5);
  const remaining = leaderboard.slice(5);

  return (
    <div className="space-y-10 px-4 py-8 md:space-y-14 md:px-8 lg:px-10 md:py-10">

      {/* ══ Page header ══ */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Community Fund{monthLabel ? ` — ${monthLabel}` : ''}
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Leftover subscriber slices roll into a shared monthly pot. Nominate the
          works you believe in, vote with your slices — at month end the entire
          pot goes to the winning work.
        </p>
      </div>

      {/* ══ Pot balance + countdown ══ */}
      <div className="relative overflow-hidden rounded-xl border border-amber-500/20 bg-amber-500/[0.04] px-5 sm:px-8 py-8">
        <div className="pointer-events-none absolute right-0 top-0 h-full w-1/2" aria-hidden>
          <div className="absolute right-8 top-1/2 -translate-y-1/2 w-[360px] h-[360px] rounded-full bg-amber-500/15 blur-[100px]" />
        </div>

        <div className="relative flex flex-col sm:flex-row gap-8 sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              In the pot this month
            </p>
            <p className="text-4xl md:text-5xl font-semibold text-amber-400 tabular-nums">
              🍕 {potBalance.toLocaleString()}
            </p>
            <p className="text-sm text-muted-foreground">
              awarded in full to the winning work
            </p>
          </div>

          {period?.ends_at && (
            <div className="space-y-1.5 sm:text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Month ends in
              </p>
              <div className="flex items-center gap-2.5 sm:justify-end">
                <CountSegment value={countdown.days} label="d" />
                <CountSegment value={countdown.hours} label="h" />
                <CountSegment value={countdown.mins} label="m" />
                <CountSegment value={countdown.secs} label="s" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══ Leaderboard ══ */}
      <section>
        <RailHeader title="This Month's Nominees" />

        {isLoading ? (
          <LeaderboardSkeleton />
        ) : leaderboard.length === 0 ? (
          <EmptyNominees />
        ) : (
          <div className="space-y-6">
            {/* #1 — highlighted */}
            <LeadCard entry={top5[0]} onVote={setVoteTarget} />

            {/* 2–5 — ranked cards */}
            {top5.length > 1 && (
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                {top5.slice(1).map((entry, i) => (
                  <RankedCard
                    key={entry.work_id}
                    entry={entry}
                    rank={i + 2}
                    onVote={setVoteTarget}
                  />
                ))}
              </div>
            )}

            {/* 6+ — compact list */}
            {remaining.length > 0 && (
              <ol className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden bg-card/20">
                {remaining.map((entry, i) => (
                  <NomineeRow
                    key={entry.work_id}
                    entry={entry}
                    rank={i + 6}
                    onVote={setVoteTarget}
                  />
                ))}
              </ol>
            )}
          </div>
        )}
      </section>

      {/* ══ Past winners ══ */}
      {!isLoading && (
        <PastFundWinners pastWorkWinners={pastWorkWinners} />
      )}

      {/* Vote modal */}
      <PotVoteModal
        isOpen={!!voteTarget}
        onClose={() => setVoteTarget(null)}
        work={voteTarget ? { id: voteTarget.work_id, title: voteTarget.title } : null}
        balance={balance}
        isActiveSubscriber={isActiveSubscriber}
        isAuthenticated={isAuthenticated}
        voteWork={voteWork}
        onSuccess={() => {
          refetch();
          setVoteTarget(null);
        }}
      />
    </div>
  );
};

export default CommunityFund;
